import { IBrandService } from './brandService'
import { LocalBrandService } from './localBrandService'
import { D1BrandService } from './d1BrandService'

export type BrandServiceType = 'local' | 'd1'

export interface BrandServiceConfig {
  type: BrandServiceType
  d1Database?: any // D1Database instance for Cloudflare Workers
}

class BrandServiceFactory {
  private static instance: BrandServiceFactory
  private currentService: IBrandService | null = null
  private config: BrandServiceConfig = { type: 'local' }
  
  private constructor() {}
  
  static getInstance(): BrandServiceFactory {
    if (!BrandServiceFactory.instance) {
      BrandServiceFactory.instance = new BrandServiceFactory()
    }
    return BrandServiceFactory.instance
  }
  
  configure(config: BrandServiceConfig): void {
    this.config = config
    this.currentService = null // Reset service to force recreation
  }
  
  getService(): IBrandService {
    if (!this.currentService) {
      this.currentService = this.createService()
    }
    return this.currentService
  }
  
  private createService(): IBrandService {
    switch (this.config.type) {
      case 'local':
        return new LocalBrandService()
      
      case 'd1':
        if (!this.config.d1Database) {
          throw new Error('D1 database instance is required for D1 brand service')
        }
        return new D1BrandService(this.config.d1Database)
      
      default:
        throw new Error(`Unsupported brand service type: ${this.config.type}`)
    }
  }
  
  // Utility methods
  getCurrentServiceType(): BrandServiceType {
    return this.config.type
  }
  
  isD1Service(): boolean {
    return this.config.type === 'd1'
  }
  
  isLocalService(): boolean {
    return this.config.type === 'local'
  }
  
  // For testing and development
  resetService(): void {
    this.currentService = null
  }
}

// Export singleton instance
export const brandServiceFactory = BrandServiceFactory.getInstance()

// Convenience function for getting the current service
export function getBrandService(): IBrandService {
  return brandServiceFactory.getService()
}

// Environment-based configuration helper
export function configureBrandService(): void {
  const isDevelopment = process.env.NODE_ENV === 'development'
  const hasD1Database = typeof (globalThis as any).D1_DATABASE !== 'undefined'
  
  if (isDevelopment || !hasD1Database) {
    // Use local service for development or when D1 is not available
    brandServiceFactory.configure({ type: 'local' })
  } else {
    // Use D1 service in production with Cloudflare Workers
    brandServiceFactory.configure({ 
      type: 'd1',
      d1Database: (globalThis as any).D1_DATABASE
    })
  }
}

// Migration utility
export async function migrateBrandService(fromType: BrandServiceType, toType: BrandServiceType): Promise<boolean> {
  try {
    // Get data from source service
    const sourceConfig: BrandServiceConfig = { type: fromType }
    if (fromType === 'd1') {
      sourceConfig.d1Database = (globalThis as any).D1_DATABASE
    }
    
    brandServiceFactory.configure(sourceConfig)
    const sourceService = brandServiceFactory.getService()
    
    const sourceBrands = await sourceService.getAllBrands()
    if (!sourceBrands.success) {
      console.error('Failed to get brands from source service:', sourceBrands.error)
      return false
    }
    
    // Setup target service
    const targetConfig: BrandServiceConfig = { type: toType }
    if (toType === 'd1') {
      targetConfig.d1Database = (globalThis as any).D1_DATABASE
    }
    
    brandServiceFactory.configure(targetConfig)
    const targetService = brandServiceFactory.getService()
    
    // Initialize D1 database if needed
    if (toType === 'd1' && targetService instanceof D1BrandService) {
      const initResult = await targetService.initializeDatabase()
      if (!initResult.success) {
        console.error('Failed to initialize D1 database:', initResult.error)
        return false
      }
    }
    
    // Migrate data
    const migrationResult = await targetService.createBulk(
      sourceBrands.data.map(brand => ({
        brand: brand.brand,
        website: brand.website,
        amazon_presence: brand.amazon_presence,
        manufacturing_verification: brand.manufacturing_verification,
        hq_location: brand.hq_location,
        mfg_locations: brand.mfg_locations.join(';'),
        primary_products: brand.primary_products,
        product_categories: brand.product_categories.join(';'),
        clean_categories: brand.clean_categories.join(','),
        purchase_methods: brand.purchase_methods,
        founded_year: brand.founded_year,
        business_type: brand.business_type,
        certifications: brand.certifications.join(';'),
        price_tier: brand.price_tier,
        consumer_summary: brand.consumer_summary,
        evidence_sources: brand.evidence_sources,
        confidence_score: brand.confidence_score,
        last_verified: brand.last_verified
      }))
    )
    
    if (!migrationResult.success) {
      console.error('Failed to migrate brands:', migrationResult.error)
      return false
    }
    
    console.log(`Successfully migrated ${migrationResult.data.length} brands from ${fromType} to ${toType}`)
    return true
    
  } catch (error) {
    console.error('Migration failed:', error)
    return false
  }
}